import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../css/editorial.css';

/* ── Edit these values ─────────────────────────────────────────────── */
const CONTACT_EMAIL  = 'theeliteglobalreview@gmail.com';                  // e.g. 'editorial@theeliteglobalreview.com'
const EFFECTIVE_DATE = 'October 13, 2026';
const LAST_UPDATED   = 'October 13, 2026';
/* ──────────────────────────────────────────────────────────────────── */

const WEBSITE_URL = 'https://theeliteglobalreview.com';

const INTRO =
  'At The Elite Global Review, we believe every business story deserves to be presented with care, accuracy, and professionalism.';

/* ── Icons (inline SVG, inherit colour from CSS) ──────────────────── */
const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const ICONS = {
  star: (
    <Icon><path d="m12 3.5 2.6 5.4 5.9.8-4.3 4.1 1 5.9-5.2-2.8-5.2 2.8 1-5.9-4.3-4.1 5.9-.8Z" /></Icon>
  ),
  doc: (
    <Icon><path d="M6 3h8l4 4v14H6Z" /><path d="M14 3v4h4M9.5 12h5M9.5 16h5" /></Icon>
  ),
  check: (
    <Icon><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4.5 4.5" /><path d="m8.5 11 1.8 1.8 3.3-3.4" /></Icon>
  ),
  mic: (
    <Icon><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3" /></Icon>
  ),
  tag: (
    <Icon><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V4.5A1.5 1.5 0 0 1 4.5 3H13l7.6 7.6a2 2 0 0 1 0 2.8Z" /><circle cx="8" cy="8" r="1.3" /></Icon>
  ),
  scales: (
    <Icon><path d="M12 4v16M7 20h10M5 7h14" /><path d="m5 7-3 7a3.2 3.2 0 0 0 6 0Z" /><path d="m19 7-3 7a3.2 3.2 0 0 0 6 0Z" /></Icon>
  ),
  image: (
    <Icon><rect x="3" y="4" width="18" height="16" rx="2.5" /><circle cx="9" cy="10" r="1.6" /><path d="m21 16-5-5-8 8" /></Icon>
  ),
  pencil: (
    <Icon><path d="M4 20v-4L16 4l4 4L8 20Z" /><path d="m13 7 4 4" /></Icon>
  ),
  users: (
    <Icon><circle cx="9" cy="8" r="3.2" /><path d="M3 20c.7-3.3 3-5 6-5s5.3 1.7 6 5" /><path d="M16 5.2a3.2 3.2 0 0 1 0 5.6M18 15c1.7.6 2.7 2.2 3 5" /></Icon>
  ),
  chat: (
    <Icon><path d="M4 5h16v11H9l-5 4Z" /></Icon>
  ),
  updates: (
    <Icon>
      <path d="M20 11a8 8 0 0 0-14.3-4.6L4 8.5" /><path d="M4 4v4.5h4.5" />
      <path d="M4 13a8 8 0 0 0 14.3 4.6l1.7-2.1" /><path d="M20 20v-4.5h-4.5" />
    </Icon>
  ),
  contact: (
    <Icon><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m4 7.5 8 6 8-6" /></Icon>
  ),
};

/* `num` = the policy's own section number; `note: true` = highlighted callout */
const SECTIONS = [
  {
    id: 'our-editorial-commitment',
    num: 1,
    title: 'Our Editorial Commitment',
    icon: 'star',
    body: [
      { text: 'At The Elite Global Review, we believe every business story deserves to be presented with care, accuracy, and professionalism. Our goal is to share meaningful stories about business leaders, entrepreneurs, companies, innovation, and professional achievements with readers around the world.' },
      { text: 'We are committed to presenting content responsibly and maintaining the trust of our readers, contributors, and business community.' },
    ],
  },
  {
    id: 'content-selection',
    num: 2,
    title: 'Content Selection',
    icon: 'doc',
    body: [
      { text: 'We publish business profiles, leadership interviews, company features, cover stories, industry insights, and other editorial content that reflects professional achievements, innovation, experience, and business developments.' },
      { text: 'Our editorial team reviews submissions for relevance, clarity, quality, and suitability for our audience. Submission does not guarantee publication.' },
    ],
  },
  {
    id: 'accuracy-and-fact-checking',
    num: 3,
    title: 'Accuracy and Fact-Checking',
    icon: 'check',
    body: [
      { text: 'We aim to ensure that the information published on our website and in our digital magazines is accurate and clearly presented.' },
      { text: 'Depending on the nature of the story, our editorial process may include reviewing submitted information, checking publicly available sources, requesting clarification, and confirming relevant details with contributors.' },
      { text: 'We encourage contributors to provide accurate information and supporting material. We may revise or withhold content when important details cannot be adequately verified.' },
    ],
  },
  {
    id: 'interviews-and-business-profiles',
    num: 4,
    title: 'Interviews and Business Profiles',
    icon: 'mic',
    body: [
      { text: 'Our interviews and business profiles aim to present the experience, work, perspectives, and achievements of the people and organizations featured.' },
      { text: 'Contributors may be asked to provide biographies, company information, photographs, interview responses, and other supporting materials. We may edit submissions for grammar, clarity, length, and presentation while seeking to preserve the intended meaning.' },
      { text: 'We do not knowingly present fabricated statements as genuine quotations.', note: true },
    ],
  },
  {
    id: 'sponsored-content-and-paid-features',
    num: 5,
    title: 'Sponsored Content and Paid Features',
    icon: 'tag',
    body: [
      { text: 'Some magazine features, promotional articles, advertisements, or business profiles may involve a commercial arrangement.' },
      { text: 'Where appropriate and required, we aim to distinguish sponsored or promotional material from independent editorial content through clear labels or disclosures.' },
      { text: 'Payment for a feature does not, by itself, establish that every claim made by the featured individual or company has been independently verified. Readers should consider the relevant disclosures when evaluating content.', note: true },
    ],
  },
  {
    id: 'editorial-independence',
    num: 6,
    title: 'Editorial Independence',
    icon: 'scales',
    body: [
      { text: 'We aim to make editorial decisions based on the relevance, quality, and suitability of content for our audience.' },
      { text: 'Commercial partnerships, advertising, or publication arrangements should not be represented as proof of independent endorsement, guaranteed business success, or verified industry ranking.' },
      { text: 'Where commercial considerations affect how a feature is presented, we seek to provide appropriate transparency.' },
    ],
  },
  {
    id: 'photographs-and-other-materials',
    num: 7,
    title: 'Use of Photographs and Other Materials',
    icon: 'image',
    body: [
      { text: 'We expect contributors to have the necessary rights or permissions for photographs, logos, documents, and other materials they submit.' },
      { text: 'We respect intellectual property rights and seek to address legitimate concerns about unauthorized or incorrectly attributed material.' },
      { text: 'Photographs and other visual materials may be edited for formatting and presentation, provided the changes do not intentionally misrepresent the subject.' },
    ],
  },
  {
    id: 'corrections-and-editorial-concerns',
    num: 8,
    title: 'Corrections and Editorial Concerns',
    icon: 'pencil',
    body: [
      { text: 'We recognize that errors can occur. If you believe an article contains inaccurate information, an incorrect attribution, or another editorial issue, please contact our team.' },
      { text: 'Please include the article link, the specific concern, and any supporting information that may help us review it.', note: true },
      { text: 'We will assess the matter and, where appropriate, correct the content, clarify the information, or update the article.' },
    ],
  },
  {
    id: 'respect-and-professional-standards',
    num: 9,
    title: 'Respect and Professional Standards',
    icon: 'users',
    body: [
      { text: 'We aim to publish content that is professional, relevant, and respectful of the people and organizations discussed.' },
      { text: 'We do not knowingly publish material intended to mislead readers, infringe intellectual property rights, or present false information as fact.' },
      { text: 'We also expect contributors to communicate honestly and provide information responsibly.' },
    ],
  },
  {
    id: 'reader-feedback',
    num: 10,
    title: 'Reader Feedback',
    icon: 'chat',
    body: [
      { text: 'Our readers play an important role in maintaining editorial quality. We welcome questions, feedback, correction requests, and suggestions that help us improve our content.' },
      { text: 'Every genuine concern deserves appropriate consideration, although we cannot guarantee that every submission or requested change will be accepted.' },
    ],
  },
  {
    id: 'updates',
    num: 11,
    title: 'Updates to This Policy',
    icon: 'updates',
    body: [
      { text: 'We may update this Editorial Policy as our publication, editorial processes, and responsibilities evolve. Any revisions will be published on this page with an updated date.' },
    ],
  },
];

const CONTACT_ID = 'contact-our-editorial-team';
const TOC = [
  ...SECTIONS.map(({ id, title, num }) => ({ id, title, num })),
  { id: CONTACT_ID, num: 12, title: 'Contact Our Editorial Team' },
];

/* Short summary cards under the hero — each one restates the policy below */
const HIGHLIGHTS = [
  { icon: 'check',   title: 'Accuracy comes first',     text: 'Details are reviewed and clarified before publishing.', to: 'accuracy-and-fact-checking' },
  { icon: 'tag',     title: 'Sponsored content labeled', text: 'Disclosed where appropriate and required.',            to: 'sponsored-content-and-paid-features' },
  { icon: 'pencil',  title: 'Corrections welcome',      text: 'Report a concern and our team will review it.',        to: 'corrections-and-editorial-concerns' },
];

export default function EditorialPolicy() {
  const [active, setActive]     = useState(TOC[0].id);
  const [progress, setProgress] = useState(0);
  const [tocStyle, setTocStyle] = useState(null);
  const tocRef     = useRef(null);
  const articleRef = useRef(null);

  /* Fix the TOC (position: fixed) once it reaches the navbar offset,
     and release it at the end of the article */
  useEffect(() => {
    const NAV = 112; // keep in sync with --ep-nav-space
    const update = () => {
      const col = tocRef.current;
      const art = articleRef.current;
      if (!col || !art || window.innerWidth <= 960) { setTocStyle(null); return; }
      const c = col.getBoundingClientRect();
      const a = art.getBoundingClientRect();
      const innerH = col.firstElementChild.offsetHeight;
      if (c.top <= NAV) {
        setTocStyle({
          position: 'fixed',
          top: Math.min(NAV, a.bottom - innerH),
          left: c.left,
          width: c.width,
        });
      } else {
        setTocStyle(null);
      }
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  /* Scroll-spy: highlight the section currently in view */
  useEffect(() => {
    const els = TOC.map(t => document.getElementById(t.id)).filter(Boolean);
    if (!('IntersectionObserver' in window) || !els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActive(visible[0].target.id);
        }
      },
      { rootMargin: '-120px 0px -60% 0px', threshold: 0 }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  /* Thin reading-progress bar */
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const h = document.documentElement;
        const max = h.scrollHeight - h.clientHeight;
        setProgress(max > 0 ? Math.min(100, (h.scrollTop / max) * 100) : 0);
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const goTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    setActive(id);
    if (window.history?.replaceState) window.history.replaceState(null, '', `#${id}`);
  };

  return (
    <main className="ep-page">
      <div className="ep-progress" aria-hidden="true">
        <span style={{ width: `${progress}%` }} />
      </div>

      {/* ── Header ───────────────────────────────────────────── */}
      <header className="ep-hero">
        <div className="ep-wrap">
          <nav className="ep-crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <span aria-current="page">Editorial Policy</span>
          </nav>

          <h1 className="ep-title">Editorial Policy</h1>
          <span className="ep-rule" aria-hidden="true" />
          <p className="ep-intro">{INTRO}</p>

          <div className="ep-meta">
            <p className="ep-updated">
              <span className="ep-updated-dot" aria-hidden="true" />
              Effective <time>{EFFECTIVE_DATE}</time>
            </p>
            <p className="ep-updated">
              <span className="ep-updated-dot" aria-hidden="true" />
              Last updated <time>{LAST_UPDATED}</time>
            </p>
          </div>
        </div>
      </header>

      {/* ── Highlights ───────────────────────────────────────── */}
      <div className="ep-wrap ep-highlights-wrap">
        <ul className="ep-highlights">
          {HIGHLIGHTS.map(h => (
            <li key={h.title}>
              <a href={`#${h.to}`} onClick={(e) => goTo(e, h.to)} className="ep-highlight">
                <span className="ep-icon">{ICONS[h.icon]}</span>
                <span className="ep-highlight-text">
                  <strong>{h.title}</strong>
                  <span>{h.text}</span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* ── Body ─────────────────────────────────────────────── */}
      <div className="ep-wrap ep-layout">
        <aside className="ep-toc" ref={tocRef} aria-label="On this page">
          <div className="ep-toc-inner" style={tocStyle || undefined}>
          <p className="ep-toc-title">On this page</p>
          <ol>
            {TOC.map((t) => (
              <li key={t.id}>
                <a
                  href={`#${t.id}`}
                  onClick={(e) => goTo(e, t.id)}
                  className={active === t.id ? 'is-active' : ''}
                  aria-current={active === t.id ? 'true' : undefined}
                >
                  <span className="ep-toc-num" aria-hidden="true">{t.num ?? '·'}</span>
                  {t.title}
                </a>
              </li>
            ))}
          </ol>
          </div>
        </aside>

        <article className="ep-article" ref={articleRef}>
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="ep-section" aria-labelledby={`${s.id}-h`}>
              <div className="ep-section-head">
                <span className="ep-icon">{ICONS[s.icon]}</span>
                <h2 id={`${s.id}-h`}>
                  {s.num && <span className="ep-h-num">{s.num}.</span>}
                  {s.title}
                </h2>
              </div>
              {s.body.map((p, j) => (
                <p key={j} className={p.note ? 'ep-note' : undefined}>{p.text}</p>
              ))}
            </section>
          ))}

          <section id={CONTACT_ID} className="ep-section ep-section-contact" aria-labelledby={`${CONTACT_ID}-h`}>
            <div className="ep-section-head">
              <span className="ep-icon">{ICONS.contact}</span>
              <h2 id={`${CONTACT_ID}-h`}>Contact Our Editorial Team</h2>
            </div>
            <p>
              For editorial inquiries, corrections, interview proposals, or concerns about
              published content, please contact:
            </p>

            <div className="ep-contact">
              <p className="ep-contact-name">The Elite Global Review</p>
              <dl>
                <div>
                  <dt>Website</dt>
                  <dd>
                    <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer">
                      theeliteglobalreview.com
                    </a>
                  </dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>
                    {CONTACT_EMAIL
                      ? <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                      : <span className="ep-placeholder">[Insert your official editorial or business email]</span>}
                  </dd>
                </div>
              </dl>

              {CONTACT_EMAIL && (
                <a className="ep-btn" href={`mailto:${CONTACT_EMAIL}`}>Email our editorial team</a>
              )}
            </div>

            <p className="ep-closing">
              Our commitment is to share business and leadership stories responsibly,
              communicate transparently, and continue earning the trust of our readers and contributors.
            </p>
          </section>
        </article>
      </div>
    </main>
  );
}